import { ECElementEvent } from 'echarts';
import moment from 'moment';
import React, { JSX, useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  AuctionInsightKey,
  AuctionInsightsData,
  getDealAuctionInsights,
} from '~/src/dash/apiV2/auctionInsights';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { Sankey } from '~/src/dash/components/charts/Sankey';
import {
  formatSankeyData,
  getSankeyNodesToExclude,
  IEChartsSankeyData,
  IEChartsSankeyLabelParams,
  IEChartsSankeyLink,
} from '~/src/dash/components/charts/Sankey/common';
import {
  formatLinkTooltip,
  formatNodeTooltip,
} from '~/src/dash/components/charts/Sankey/customTooltips';
import { highlightedDivStyle, spacing } from '~/src/dash/components/layout/common';
import { Row, Rows, TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  findAuctionInsightsTimeRangeByKey,
  ITimeRange,
  TimeRange,
} from '~/src/dash/utils/dateTimeRanges';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { PUB_OPS_TEAM_IDS } from '~/src/dash/utils/users';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { dealAuctionInsightsNodes } from '~/src/dash/views/deal/DealAuctionInsights/config';
import { DealAuctionInsightsFilters } from '~/src/dash/views/deal/DealAuctionInsights/DealAuctionInsightsFilters';

interface IProps {
  dealID: number;
  calStartDateStr: string;
  calEndDateStr: string;
  startDate: string;
  endDate: string;
  handleApplyDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
}

const DealAuctionInsightsComponent = ({
  dealID,
  calStartDateStr,
  calEndDateStr,
  startDate = '',
  endDate = '',
  handleApplyDatesChange,
}: IProps): JSX.Element => {
  const { session, isSuperUser, user } = useBaseStore();
  const { t } = usePublicaTranslation();

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [nodesToExclude, setNodesToExclude] = useState<string[]>([]);
  const [data, setData] = useState<IEChartsSankeyData>({ nodes: [], links: [] });
  const [isRealtimeOnly, setIsRealtimeOnly] = useState<boolean>(true);
  const [realTimeRange, setRealTimeRange] = useState<ITimeRange>(
    findAuctionInsightsTimeRangeByKey('24 Hours'),
  );

  const hasDealStarted = startDate ? !moment().isBefore(startDate) : true;

  const fetchData = useCallback((): void => {
    const queryStart = moment().unix();
    setLoadState(LoadingStateLoading);
    getDealAuctionInsights(session.publisher.ID, {
      PublisherID: Number(session.publisher.ID),
      DealID: dealID,
      StartDate: calStartDateStr,
      EndDate: calEndDateStr,
      Timezone: session.publisher.Timezone,
      LiveDensityRange: isRealtimeOnly ? realTimeRange.value.toString() : '',
      IsRealTimeOnly: isRealtimeOnly,
    })
      .then((data: AuctionInsightsData) => {
        const formattedData = data
          ? formatSankeyData(
              data,
              nodesToExclude,
              dealAuctionInsightsNodes,
              publicaColors.lightGray,
            )
          : { nodes: [], links: [] };
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setData(formattedData);
        });
        track(
          'Deal Auction Insights - Data Retrieved',
          { isSuperUser, queryTime: moment().unix() - queryStart },
          user.user,
          PUB_OPS_TEAM_IDS,
        );
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setData({ nodes: [], links: [] });
        });
        console.error(err);
      });
  }, [calStartDateStr, calEndDateStr, nodesToExclude, realTimeRange, isRealtimeOnly]);

  const handleNodeClick = useCallback((params: ECElementEvent): void => {
    const excludedNodes = getSankeyNodesToExclude(params.name, dealAuctionInsightsNodes);
    setNodesToExclude(excludedNodes);
  }, []);

  const handleLinkClick = useCallback((params: { data: IEChartsSankeyLink }): void => {
    const sourceKey = params.data?.source;
    if (sourceKey) {
      const excludedNodes = getSankeyNodesToExclude(sourceKey, dealAuctionInsightsNodes);
      setNodesToExclude(excludedNodes);
    }
  }, []);

  const handleResetDrilldown = useCallback((): void => {
    setNodesToExclude([]);
  }, []);

  const labelFormatter = useCallback(
    (params: IEChartsSankeyLabelParams) => {
      const nodeConfig = dealAuctionInsightsNodes[params.name as AuctionInsightKey];
      const label = nodeConfig?.tKey ? t(nodeConfig.tKey) : params.name;
      const nodeData = params.data.nodeData;
      const depth = params.data.depth ?? 0;
      const percentage: number = (nodeData?.percentage as number) ?? 0;
      const formattedPercentage: string = percentage.toFixed(2);
      const originalValue = (nodeData?.total as number) ?? 0;
      const formattedValue = originalValue.toLocaleString('en-US');
      if (depth === 0) {
        return `{labelName|${label}}\n{labelValue|${formattedValue}}`;
      }
      return `{labelName|${label}}\n{labelValue|${formattedValue} - ${formattedPercentage}%}`;
    },
    [t],
  );

  const handleToggleRealTimeRange = (rangeKey: TimeRange): void => {
    setRealTimeRange(findAuctionInsightsTimeRangeByKey(rangeKey));
    track(
      'Deal Auction Insights - Select Real Time Range',
      { isSuperUser, range: rangeKey },
      user.user,
    );
  };

  const handleToggleRealtimeOnly = (): void => {
    setIsRealtimeOnly(!isRealtimeOnly);
  };

  const customNoDataMessageTKey: TKey = hasDealStarted
    ? ('NoDataToDisplay' as TKey)
    : ('DataWillBeAvailableOnceTheDealBegins' as TKey);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Rows>
      <Row noHorizontalPadding paddingTop={spacing.none} paddingBottom={spacing.small}>
        <DealAuctionInsightsFilters
          calStartDate={moment(calStartDateStr)}
          calEndDate={moment(calEndDateStr)}
          handleApplyDatesChange={handleApplyDatesChange}
          loadState={loadState}
          dealStartDate={startDate}
          dealEndDate={endDate}
          realTimeRange={realTimeRange}
          handleToggleRealTimeRange={handleToggleRealTimeRange}
          isRealtimeOnly={isRealtimeOnly}
          handleToggleRealtimeOnly={handleToggleRealtimeOnly}
        />
      </Row>
      <Row noPadding>
        <Rows style={{ ...highlightedDivStyle }}>
          <Row>
            <Sankey
              titleTKey='AuctionInsights'
              loadState={loadState}
              nodes={data.nodes}
              links={data.links}
              labelFormatter={labelFormatter}
              nodeTooltipFormatter={formatNodeTooltip}
              linkTooltipFormatter={formatLinkTooltip}
              nodeConfigMap={dealAuctionInsightsNodes}
              customNoDataMessageTKey={customNoDataMessageTKey}
              onNodeClick={handleNodeClick}
              onLinkClick={handleLinkClick}
              onResetDrilldown={handleResetDrilldown}
              isResetActive={nodesToExclude.length > 0}
              messageTKey='SankeyNotice'
            />
          </Row>
        </Rows>
      </Row>
    </Rows>
  );
};

// Wrap in React.memo to prevent re-renders when parent updates but props haven't changed
export const DealAuctionInsights = React.memo(DealAuctionInsightsComponent);
